import { addDays } from 'date-fns';
import { LDFlagSet } from 'launchdarkly-js-client-sdk';
import { Channel, BiogenFunction, TacticSuffix, TacticStatus } from 'basics/enums/maya.enums';
import { DropMaterialsOptionType } from 'basics/types/dropMaterials.types';
import { KeyMessagesOptionType } from 'basics/types/keyMessages.types';
import { EventState } from 'basics/types/maya.types';
import { formatUTCDate } from 'basics/utils/date.utils';
import { transformInputDateUTC } from 'basics/utils/dates/dates.utils';
import { removeValueFromString } from 'basics/utils/removeValueFromString';
import { defaultTacticVersion, maxTacticTitleLength } from 'components/TacticForm/TacticForm.constants';
import {
  Channel as ServerChannel,
  BulkUpdateTacticInput,
  CustomerEngagementPlanning,
  Tactic,
  TacticInput,
  Channel as TacticChannel,
} from 'generated/maya.types';
import { YodaFieldValue } from 'yoda-ui/yodaForm/yodaForm.types';

export const tacticToBE = (
  data: YodaFieldValue,
  cepId: string | undefined,
  keyMessagesOptions: KeyMessagesOptionType[],
  dropMaterialsOptions: DropMaterialsOptionType[],
  version: number,
): Partial<TacticInput> => {
  const keyMessage = keyMessagesOptions.filter(({ id }) => id === data.keyMessage);
  const dropMaterial = dropMaterialsOptions.filter(({ id }) => id === data.dropMaterial);
  const titleSuffix = data.titleSuffix ? ` ${data.titleSuffix}` : '';
  const tacticTitleWithSuffix = `${data.title}${titleSuffix}`;

  return {
    title: tacticTitleWithSuffix.length > maxTacticTitleLength ? data.title : tacticTitleWithSuffix,
    channel: data.channel,
    productionOwner: data.productionOwner,
    ...transformInputDateUTC(data.date),
    cep: cepId,
    executingTeam: data.executingTeam,
    description: data.description,
    mandatory: data.mandatory,
    includeConsent: data.includeConsent,
    previousTactic: data.previousTactic || null,
    parentTactic: data.tacticGroup || data.parentTactic || null,
    assetName: data.assetName,
    callToAction: data.callToAction,
    reachMetric: data.reachMetric,
    reachValue: parseInt(data?.reachValue, 10) || undefined,
    engagementMetric: data.engagementMetric,
    engagementValue: parseInt(data?.engagementValue, 10) || undefined,
    keyMessage: keyMessage.length ? {
      id: keyMessage[0].id.toString(),
      name: keyMessage[0].label,
    } : {
      id: '',
      name: '',
    },
    dropMaterial: dropMaterial.length ? {
      id: dropMaterial[0].id,
      name: dropMaterial[0].label,
    } : {
      id: '',
      name: '',
    },
    approvedDocumentId: data.approvedDocumentId,
    eventId: data.eventId,
    actionOrder: !Number.isNaN(parseInt(data?.actionOrder, 10)) ? parseInt(data?.actionOrder, 10) : undefined,
    destinationUrl: data.destinationUrl,
    isKeyPerformanceIndicator: data.isKeyPerformanceIndicator || false,
    IVADocumentId: data.IVADocumentId,
    comment: data.comment || undefined,
    version,
  };
};

export const formValuesToTactic = (
  formValues: YodaFieldValue,
  cep: CustomerEngagementPlanning,
  parentTactic: Tactic | undefined,
  previousTactic: Tactic | undefined,
): Tactic => {
  const array = new Uint8Array(12);
  crypto.getRandomValues(array);
  const tacticId = Array.from(array, (byte) => byte.toString(16).padStart(2, '0')).join('');

  return {
    _id: tacticId,
    actionOrder: formValues.actionOrder,
    approvedDocumentId: formValues.approvedDocumentId,
    assetName: formValues.assetName,
    callToAction: formValues.callToAction,
    cep,
    channel: formValues.channel,
    createdBy: '',
    dateEnd: formValues.dateEnd,
    dateStart: formValues.dateStart,
    description: formValues.description,
    destinationUrl: formValues.destinationUrl,
    dropMaterial: formValues.dropMaterial,
    engagementMetric: null,
    engagementValue: null,
    eventId: formValues.eventId,
    executingTeam: formValues.executingTeam,
    formattedTacticId: `Tactic-${tacticId}`,
    includeConsent: formValues.includeConsent,
    isKeyPerformanceIndicator: formValues.isKeyPerformanceIndicator || false,
    keyMessage: formValues.keyMessage,
    lastModifiedBy: '',
    lastVeevaSync: null,
    lastVeevaSyncError: null,
    mandatory: formValues.mandatory,
    parentTactic,
    previousTactic,
    previousTacticVersionId: null,
    productionOwner: null,
    reachMetric: null,
    reachValue: null,
    status: TacticStatus.DRAFT,
    tacticId: Date.now(),
    title: formValues.title,
    updated: new Date().toISOString(),
    veevaSyncStatus: null,
    version: defaultTacticVersion,
    IVADocumentId: formValues.IVADocumentId,
    comment: formValues.comment || undefined,
  };
};

export const tacticToBulkUpdateInput = (
  tactic: Tactic,
  cepId: string | undefined,
): BulkUpdateTacticInput => {
  return {
    _id: tactic._id,
    title: tactic?.title,
    channel: Channel[tactic.channel as unknown as keyof typeof Channel],
    productionOwner: tactic?.productionOwner || '',
    description: tactic?.description || '',
    cep: cepId,
    dateEnd: tactic?.dateEnd,
    dateStart: tactic?.dateStart,
    executingTeam: tactic.executingTeam,
    mandatory: tactic?.mandatory || false,
    includeConsent: tactic?.includeConsent !== undefined ? tactic?.includeConsent : true,
    previousTactic: tactic?.previousTactic?._id || null,
    parentTactic: tactic?.parentTactic?._id || null,
    assetName: tactic?.assetName || '',
    callToAction: tactic?.callToAction || undefined,
    reachMetric: tactic?.reachMetric,
    reachValue: tactic?.reachValue,
    engagementMetric: tactic?.engagementMetric,
    engagementValue: tactic?.engagementValue,
    keyMessage: tactic?.keyMessage || undefined,
    dropMaterial: tactic?.dropMaterial || undefined,
    approvedDocumentId: tactic?.approvedDocumentId || '',
    eventId: tactic?.eventId || '',
    actionOrder: tactic?.actionOrder,
    destinationUrl: tactic?.destinationUrl || '',
    isKeyPerformanceIndicator: tactic?.isKeyPerformanceIndicator || false,
    IVADocumentId: tactic?.IVADocumentId || '',
  };
};

export const tacticToTacticInput = (
  tactic: Tactic,
  cepId: string,
): TacticInput => {
  return {
    channel: Channel[tactic.channel as unknown as keyof typeof Channel],
    title: tactic?.title,
    cep: cepId,
    dateEnd: tactic?.dateEnd,
    dateStart: tactic?.dateStart,
    productionOwner: tactic?.productionOwner || '',
    executingTeam: tactic.executingTeam,
    description: tactic?.description || '',
    mandatory: tactic?.mandatory || false,
    includeConsent: tactic?.includeConsent !== undefined ? tactic?.includeConsent : true,
    previousTactic: tactic?.previousTactic?._id || null,
    parentTactic: tactic?.parentTactic?._id || null,
    assetName: tactic?.assetName || '',
    callToAction: tactic?.callToAction || undefined,
    reachValue: tactic?.reachValue,
    reachMetric: tactic?.reachMetric,
    engagementMetric: tactic?.engagementMetric,
    keyMessage: tactic?.keyMessage || undefined,
    dropMaterial: tactic?.dropMaterial || undefined,
    engagementValue: tactic?.engagementValue,
    approvedDocumentId: tactic?.approvedDocumentId || '',
    actionOrder: tactic?.actionOrder,
    eventId: tactic?.eventId || '',
    isKeyPerformanceIndicator: tactic?.isKeyPerformanceIndicator || false,
    destinationUrl: tactic?.destinationUrl || '',
    version: tactic?.version || defaultTacticVersion,
    IVADocumentId: tactic?.IVADocumentId || '',
  };
};

export const tacticGroupToBE = (
  title: string,
  cepId: string | undefined,
  version: number,
  status: string,
): Partial<TacticInput> => {
  return {
    title,
    cep: cepId,
    channel: Channel.group,
    executingTeam: BiogenFunction.Commercial,
    status,
    version,
  };
};

export const tacticToForm = (
  tactic: Tactic | undefined | null,
  cep: Partial<CustomerEngagementPlanning> | undefined | null,
  flags: LDFlagSet,
) => {
  const previousTactic = tactic?.previousTactic as Tactic;
  const parentTactic = tactic?.parentTactic as Tactic;
  const dateDefaultValue = tactic?.dateStart
    ? formatUTCDate(tactic?.dateStart, tactic?.dateEnd)
    : formatUTCDate(cep?.dateStart);

  const isBEChannel = TacticChannel.broadcastEmail === tactic?.channel;
  const isAEChannel = TacticChannel.approvedEmail === tactic?.channel;
  const titleWithoutSuffix = removeValueFromString(tactic?.title || '', TacticSuffix);
  return {
    title: ((isBEChannel || isAEChannel) && flags.mayaAkma297EnableSuffixForTacticName ? titleWithoutSuffix : tactic?.title) || '',
    titleSuffix: flags.mayaAkma297EnableSuffixForTacticName && tactic?.callToAction ? TacticSuffix[tactic.callToAction] : '',
    channel: tactic?.channel ? Channel[tactic.channel as unknown as keyof typeof Channel] : undefined,
    productionOwner: tactic?.productionOwner || '',
    date: dateDefaultValue,
    description: tactic?.description,
    cep: tactic?.cep || {},
    executingTeam: tactic?.executingTeam || '',
    mandatory: tactic?.mandatory || false,
    includeConsent: tactic?.includeConsent !== undefined ? tactic?.includeConsent : true,
    previousTactic: previousTactic?._id || null,
    parentTactic: parentTactic?._id || null,
    assetName: tactic?.assetName,
    callToAction: tactic?.callToAction,
    reachMetric: tactic?.reachMetric,
    reachValue: tactic?.reachValue?.toString(),
    engagementMetric: tactic?.engagementMetric,
    engagementValue: tactic?.engagementValue?.toString(),
    keyMessage: tactic?.keyMessage?.id || '',
    dropMaterial: tactic?.dropMaterial?.id || '',
    approvedDocumentId: tactic?.approvedDocumentId || '',
    eventId: tactic?.eventId || '',
    actionOrder: tactic?.actionOrder?.toString(),
    destinationUrl: tactic?.destinationUrl || '',
    isKeyPerformanceIndicator: tactic?.isKeyPerformanceIndicator || false,
    IVADocumentId: tactic?.IVADocumentId || '',
    comment: tactic?.comment || '',
  };
};

export const transformTacticTitle = (tactic: Tactic, flags: LDFlagSet) => {
  if (!tactic) {
    return tactic;
  }

  const isBEChannel = TacticChannel.broadcastEmail === tactic?.channel;
  const isAEChannel = TacticChannel.approvedEmail === tactic?.channel;
  let { title } = tactic;

  if ((isBEChannel || isAEChannel) && flags.mayaAkma297EnableSuffixForTacticName) {
    const titleWithoutSuffix = removeValueFromString(tactic?.title || '', TacticSuffix);
    const suffix = tactic?.callToAction ? TacticSuffix[tactic.callToAction] : '';
    title = suffix ? `${titleWithoutSuffix} ${suffix}` : tactic.title;
  }

  return {
    ...tactic,
    title,
  };
};

export const eventToTactic = (cep: CustomerEngagementPlanning, tactic: Tactic, event: EventState): BulkUpdateTacticInput => {
  const tacticInput = tacticToBulkUpdateInput(tactic, cep._id);
  const { channel } = tactic;

  if (channel === ServerChannel.broadcastEmail) {
    return {
      ...tacticInput,
      dateStart: cep.dateStart,
      dateEnd: cep.dateEnd,
      assetName: event.assetName,
      executingTeam: BiogenFunction.Commercial,
    };
  }

  if (channel === ServerChannel.webLogIn) {
    return {
      ...tacticInput,
      dateStart: cep.dateStart,
      dateEnd: cep.dateEnd,
      description: event.assetName,
      destinationUrl: event.eventURL,
      executingTeam: BiogenFunction.Commercial,
    };
  }

  if (channel === ServerChannel.virtualEvent || channel === ServerChannel.f2fEvent || channel === ServerChannel.hybridEvent) {
    const eventStartDate = new Date(event.startDate);
    const eventEndDate = new Date(event.endDate);
    return {
      ...tacticInput,
      ...transformInputDateUTC([eventStartDate, addDays(eventEndDate, 1)]),
      eventId: event.eventId,
    };
  }

  return tacticInput;
};
